import React, { useState, useEffect, useRef, useCallback } from "react";
import { Input } from "./ui/input";
import { Loader2, MapPin } from "lucide-react";

// Single global loader for the Google Maps JS SDK (Places library)
let googleLoaderPromise = null;
function loadGoogleMaps(apiKey) {
  if (typeof window === "undefined") return Promise.reject(new Error("no window"));
  if (window.google?.maps?.places) return Promise.resolve(window.google);
  if (googleLoaderPromise) return googleLoaderPromise;
  googleLoaderPromise = new Promise((resolve, reject) => {
    const existing = document.getElementById("google-maps-sdk");
    if (existing) {
      existing.addEventListener("load", () => resolve(window.google));
      existing.addEventListener("error", () => reject(new Error("google-maps-load-failed")));
      return;
    }
    const s = document.createElement("script");
    s.id = "google-maps-sdk";
    s.async = true;
    s.defer = true;
    s.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&libraries=places&v=quarterly&language=nl&region=NL`;
    s.onload = () => resolve(window.google);
    s.onerror = () => {
      googleLoaderPromise = null;
      reject(new Error("google-maps-load-failed"));
    };
    document.head.appendChild(s);
  });
  return googleLoaderPromise;
}

/**
 * Uber/Bolt-style address autocomplete using Google Places.
 * Falls back gracefully (returns null and parent should swap) if no key configured.
 *
 * Selected value shape:
 *   { label: "Full formatted address", lat: 52.379, lon: 4.900, place_id: "Ch..." }
 */
export default function GoogleAddressInput({ value, onChange, placeholder, testid }) {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";
  const [q, setQ] = useState(value?.label || "");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const [ready, setReady] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const acService = useRef(null);
  const placesService = useRef(null);
  const sessionToken = useRef(null);
  const debounceTimer = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => { setQ(value?.label || ""); }, [value?.label]);

  // Load SDK once
  useEffect(() => {
    if (!apiKey) { setLoadError(true); return; }
    let mounted = true;
    loadGoogleMaps(apiKey)
      .then((g) => {
        if (!mounted) return;
        acService.current = new g.maps.places.AutocompleteService();
        // PlacesService requires an HTMLElement to render attributions into
        const node = document.createElement("div");
        placesService.current = new g.maps.places.PlacesService(node);
        sessionToken.current = new g.maps.places.AutocompleteSessionToken();
        setReady(true);
      })
      .catch(() => mounted && setLoadError(true));
    return () => { mounted = false; };
  }, [apiKey]);

  const propagateTyped = (text) => {
    setQ(text);
    if (!text) { onChange(null); setResults([]); setOpen(false); return; }
    if (text !== value?.label) onChange({ label: text, lat: null, lon: null });
  };

  // Debounced predictions fetch
  useEffect(() => {
    if (!ready || !q || q.length < 2) { setResults([]); setOpen(false); return; }
    clearTimeout(debounceTimer.current);
    setLoading(true);
    debounceTimer.current = setTimeout(() => {
      acService.current.getPlacePredictions(
        {
          input: q,
          sessionToken: sessionToken.current,
          componentRestrictions: { country: ["nl"] },
          language: "nl",
          // types: ["geocode"], // omit to allow establishments + addresses
        },
        (preds, status) => {
          setLoading(false);
          if (status !== window.google.maps.places.PlacesServiceStatus.OK || !preds) {
            setResults([]);
            setOpen(false);
            return;
          }
          setResults(preds);
          setOpen(preds.length > 0);
          setHighlight(-1);
        }
      );
    }, 180);
    return () => clearTimeout(debounceTimer.current);
  }, [q, ready]);

  const selectPrediction = useCallback((p) => {
    if (!p || !placesService.current) return;
    placesService.current.getDetails(
      {
        placeId: p.place_id,
        fields: ["formatted_address", "geometry.location", "name", "place_id"],
        sessionToken: sessionToken.current,
      },
      (place, status) => {
        if (status !== window.google.maps.places.PlacesServiceStatus.OK || !place) return;
        const loc = place.geometry?.location;
        const label = place.formatted_address || place.name || p.description;
        onChange({
          label,
          lat: loc?.lat?.() ?? null,
          lon: loc?.lng?.() ?? null,
          place_id: place.place_id,
        });
        setQ(label);
        setOpen(false);
        // Mint a new session token for next selection (per Google billing rules)
        if (window.google?.maps?.places) {
          sessionToken.current = new window.google.maps.places.AutocompleteSessionToken();
        }
      }
    );
  }, [onChange]);

  const onKeyDown = (e) => {
    if (!open || results.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      if (highlight >= 0) {
        e.preventDefault();
        selectPrediction(results[highlight]);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  // If SDK failed to load, fall back: just behave like a plain free-form input
  return (
    <div className="relative">
      <Input
        ref={inputRef}
        data-testid={testid}
        value={q}
        onChange={(e) => propagateTyped(e.target.value)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        onFocus={() => results.length && setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
        className="bg-[#040814] border-white/20 text-white focus:border-[#D4AF37] h-12 pl-10"
        autoComplete="off"
      />
      <MapPin className="w-4 h-4 absolute left-3 top-4 text-[#D4AF37]/70" />
      {loading && <Loader2 className="w-4 h-4 absolute right-3 top-4 animate-spin text-[#D4AF37]" />}
      {open && results.length > 0 && (
        <div className="absolute z-30 mt-1 w-full bg-[#0A1128] border border-white/15 rounded-md shadow-2xl max-h-80 overflow-y-auto">
          {results.map((p, i) => {
            const main = p.structured_formatting?.main_text || p.description;
            const secondary = p.structured_formatting?.secondary_text || "";
            return (
              <button
                type="button"
                key={p.place_id}
                data-testid={`${testid}-suggestion-${i}`}
                onMouseDown={(e) => { e.preventDefault(); selectPrediction(p); }}
                onMouseEnter={() => setHighlight(i)}
                className={`w-full text-left px-4 py-2.5 text-sm border-b border-white/5 last:border-0 ${
                  highlight === i ? "bg-white/10" : "hover:bg-white/5"
                }`}
              >
                <div className="text-white/95 font-medium leading-tight">{main}</div>
                {secondary && <div className="text-white/40 text-xs mt-0.5">{secondary}</div>}
              </button>
            );
          })}
        </div>
      )}
      {loadError && !apiKey && (
        <p className="text-[11px] text-amber-300/70 mt-1" data-testid={`${testid}-warn`}>
          Address autocomplete unavailable. Type the full address and continue.
        </p>
      )}
    </div>
  );
}
